import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { Field, useFormikContext } from 'formik';
import { useMemo } from 'react';

import { ListItemText } from 'common/components/ListItem';
import {
    IGroupConditionInput,
    IOldGroupConditionInput,
    TGroupListItem,
} from 'user/types';
import { TextField } from 'common/components/FormField';
import { IOfferGroupConditionMinMaxInput } from 'graphql/types';
import { TOfferEventValues } from 'offer/types';

interface IProps {
    extraCategoryValues:
        | IGroupConditionInput['extraCategoryValues']
        | IOldGroupConditionInput['extraCategoryValues'];
    groupConditionInput: IGroupConditionInput | IOldGroupConditionInput;
    groupConditionInputIndex: number;
    group: TGroupListItem;
    showMinMaxFilters: boolean;
}

export const GroupConditionGroupExtendedListItem = ({
    groupConditionInput,
    groupConditionInputIndex,
    group,
    showMinMaxFilters,
}: IProps) => {
    const [translate] = useTranslation();
    const { values, setFieldValue } = useFormikContext<TOfferEventValues>();

    const { id: groupId, name: groupName } = group;

    // The Index of the current minmax filter for the group
    const currentMinMaxFilterIndex = useMemo(
        () =>
            groupConditionInput.minMaxFilters?.findIndex(
                (minMaxFilter) => minMaxFilter.groupId === groupId
            ),
        [groupConditionInput.minMaxFilters, groupId]
    );

    const currentMinMaxFilter = useMemo(
        () =>
            currentMinMaxFilterIndex !== undefined
                ? groupConditionInput.minMaxFilters?.[currentMinMaxFilterIndex]
                : undefined,
        [groupConditionInput.minMaxFilters, currentMinMaxFilterIndex]
    );

    const handleChangeMinMax = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (
            (!!currentMinMaxFilter &&
                currentMinMaxFilter[
                    name as keyof IOfferGroupConditionMinMaxInput
                ] === value) ||
            isNaN(+value)
        ) {
            return;
        }

        const newMinMaxFilters = [...(groupConditionInput.minMaxFilters || [])];

        const isMin = name.endsWith('min');

        // If no minmax filters are found we create a new one
        if (!currentMinMaxFilter) {
            newMinMaxFilters.push({
                groupId,
                min: isMin ? +value : undefined,
                max: isMin ? undefined : +value,
            });
        } else if (currentMinMaxFilterIndex !== undefined) {
            // Update the current min/max filter
            newMinMaxFilters[currentMinMaxFilterIndex] = {
                ...currentMinMaxFilter,
                min: isMin ? +value : currentMinMaxFilter.min,
                max: isMin ? currentMinMaxFilter.max : +value,
            };
        }

        // Update the group condition input with the new min/max filters
        const newGroupConditions = [...(values.groupConditions || [])];
        newGroupConditions[groupConditionInputIndex] = {
            ...(groupConditionInput as IGroupConditionInput),
            minMaxFilters: newMinMaxFilters,
        };

        setFieldValue('groupConditions', newGroupConditions);
    };

    return (
        <ListItem alignItems="flex-start" sx={{ p: 0.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                }}
            >
                <ListItemText>{groupName}</ListItemText>

                {showMinMaxFilters && (
                    <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
                        <Field
                            component={TextField}
                            label={translate('groupConditionFilter.min')}
                            name={`groupConditions[${groupConditionInputIndex}].minMaxFilters[${currentMinMaxFilterIndex}].min`}
                            placeholder="0"
                            slotProps={{
                                htmlInput: {
                                    sx: { width: 40, height: 7 },
                                },
                            }}
                            value={currentMinMaxFilter?.min ?? ''}
                            onChange={handleChangeMinMax}
                        />

                        <Field
                            component={TextField}
                            label={translate('groupConditionFilter.max')}
                            name={`groupConditions[${groupConditionInputIndex}].minMaxFilters[${currentMinMaxFilterIndex}].max`}
                            placeholder="0"
                            slotProps={{
                                htmlInput: {
                                    sx: { width: 40, height: 7 },
                                },
                            }}
                            value={currentMinMaxFilter?.max ?? ''}
                            onChange={handleChangeMinMax}
                        />
                    </Stack>
                )}
            </Box>
        </ListItem>
    );
};
